import { styled } from '@linaria/react';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { CloseRightPanelCommand } from '../../../core/mapV2/commands/CloseRightPanelCommand';
import { SelectNeighborCommand } from '../../../core/mapV2/commands/SelectNeighborCommand';
import { neighborSectionsQuery, rightPanelQuery } from '../../../core/mapV2/queries';
import theme from '../../../design/theme';
import { useAppDispatch, useAppSelector } from '../../../types';
import { MultiSelectionPanel } from '../panels/segment/MultiSelectionPanel';
import { SegmentSheet } from '../panels/segment/SegmentSheet';

const WIDTH = '400px';
const SLIDE = '250ms';

// The panel takes space next to the map (it does not overlay it), sliding in over 250 ms.
// While closed it is also `visibility: hidden` (after the slide-out) so its buttons cannot be reached
// with the keyboard.
const Panel = styled.aside`
    flex: 0 0 0;
    width: 0;
    overflow: hidden;
    visibility: hidden;
    box-sizing: border-box;
    background-color: ${theme.color.white.main};
    border-left: 1px solid transparent;
    transition: width ${SLIDE} ease-out, flex-basis ${SLIDE} ease-out, visibility 0s linear ${SLIDE};

    &[data-open='true'] {
        flex-basis: ${WIDTH};
        width: ${WIDTH};
        visibility: visible;
        border-left-color: ${theme.color.grey.medium};
        transition-delay: 0s;
    }
`;

// Fixed width so the content does not reflow while the panel is sliding.
const Inner = styled.div`
    width: ${WIDTH};
    height: 100%;
    display: flex;
    flex-direction: column;
`;

const Header = styled.div`
    display: flex;
    align-items: center;
    flex: 0 0 48px;
    padding: 0 8px 0 16px;
    border-bottom: 1px solid ${theme.color.grey.medium};
`;

const Title = styled.span`
    flex: 1;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: ${theme.color.grey.dark};
`;

const Body = styled.div`
    flex: 1;
    overflow-y: auto;
`;

export const RightPanel: FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { open, mode } = useAppSelector(rightPanelQuery);
    const { previous, next } = useAppSelector(neighborSectionsQuery);

    return (
        <Panel data-testid="map-v2-right-panel" data-open={open} data-mode={mode} aria-hidden={!open}>
            <Inner>
                <Header>
                    <Title>{t('mapV2.rightPanel.title')}</Title>
                    {/* Previous / next segment on the same axis: only for a single segment, disabled at the
                        end of the axis. */}
                    {mode === 'single' ? (
                        <>
                            <IconButton
                                size="small"
                                disabled={!previous}
                                onClick={() => dispatch(SelectNeighborCommand('previous'))}
                                aria-label={t('mapV2.rightPanel.previous')}
                                title={t('mapV2.rightPanel.previous')}
                            >
                                <ChevronLeftIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                                size="small"
                                disabled={!next}
                                onClick={() => dispatch(SelectNeighborCommand('next'))}
                                aria-label={t('mapV2.rightPanel.next')}
                                title={t('mapV2.rightPanel.next')}
                            >
                                <ChevronRightIcon fontSize="small" />
                            </IconButton>
                        </>
                    ) : null}
                    <IconButton
                        size="small"
                        aria-label={t('mapV2.rightPanel.close')}
                        title={t('mapV2.rightPanel.close')}
                        onClick={() => dispatch(CloseRightPanelCommand())}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Header>
                <Body>
                    {mode === 'single' ? <SegmentSheet /> : null}
                    {mode === 'multi' ? <MultiSelectionPanel /> : null}
                </Body>
            </Inner>
        </Panel>
    );
};
